import PropTypes from "prop-types";
import { memo, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import "./payment-identity-fieldset.scss";
import { CIVILITY } from "app/constants";
import SelectFormik from "app/pages/.shared/form/SelectFormik";
import InputFormik from "app/pages/.shared/form/InputFormik";
import { useFormikContext } from "formik";
import { dependantPassengersNameFields } from "app/pages/Booking/Payment/paymentSchema";
import { isEmpty } from "lodash";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import last from "lodash/last";

const PaymentIdentityFieldset = ({ id, isRequired }) => {
	const { setFieldError, errors, values, setFieldValue } = useFormikContext();

	const useContactAsFirstPassenger = values?.reuseContactInfo;
	const contactInfo = values?.contact;
	const firstPassenger = "passengers[0]";

	useEffect(() => {
		if (useContactAsFirstPassenger) {
			setFieldValue(`${firstPassenger}.name`, {
				gender: contactInfo?.["honorific-prefix"],
				firstName: contactInfo?.["given-name"],
				lastName: contactInfo?.["family-name"],
			});
		}
	}, [useContactAsFirstPassenger, contactInfo]);

	// Utilisez une référence pour stocker la dernière valeur de errors afin de résoudre les problèmes de synchronisation
	// lors de l'utilisation directe de errors dans handlePassengersNameFieldsChange
	const latestErrors = useRef(errors);

	useEffect(() => {
		latestErrors.current = errors;
	}, [errors]);

	const handlePassengersNameFieldsChange = event => {
		const { name: touchedFieldName } = event.target;
		const name = last(touchedFieldName.split("."));

		dependantPassengersNameFields.forEach(fieldName => {
			const passengerError = get(latestErrors.current, `${id}.name.${fieldName}`);
			const uniqueFullNameError = { id: "error.passengers.unique.invalid" };
			if (
				fieldName !== name &&
				!isEmpty(passengerError) &&
				isEqual(passengerError, uniqueFullNameError)
			) {
				setFieldError(`${id}.name.${fieldName}`);
			}
		});
	};

	return (
		<div className="payment-identity">
			<div className="payment-identity__group">
				<div className="payment-identity__gender payment-identity__gender--select">
					<SelectFormik
						disabled={useContactAsFirstPassenger && id === firstPassenger}
						isRequired={isRequired}
						name={`${id}.name.gender`}
						id={`${id}-gender`}
						label={<FormattedMessage id="identity.gender.label" />}
						data-testid={`payment-gender-${id}`}
					>
						<option />
						{[CIVILITY.MALE, CIVILITY.FEMALE].map(gender => (
							<FormattedMessage
								key={gender}
								id={`general.gender.${gender.toLowerCase()}`}
							>
								{message => (
									<option key={gender} value={gender}>
										{message}
									</option>
								)}
							</FormattedMessage>
						))}
					</SelectFormik>
				</div>
			</div>
			<div className="payment-identity__group">
				<div className="payment-identity__lastname payment-identity__input">
					<InputFormik
						disabled={useContactAsFirstPassenger && id === firstPassenger}
						isRequired={isRequired}
						label={<FormattedMessage id="identity.lastname.label.full" />}
						name={`${id}.name.lastName`}
						id={`${id}-lastname`}
						maxLength={30}
						data-testid={`payment-lastname-${id}`}
						onChange={handlePassengersNameFieldsChange}
					/>
				</div>

				<div className="payment-identity__firstname payment-identity__input">
					<InputFormik
						disabled={useContactAsFirstPassenger && id === firstPassenger}
						isRequired={isRequired}
						label={<FormattedMessage id="identity.firstname.label" />}
						name={`${id}.name.firstName`}
						id={`${id}-firstname`}
						maxLength={30}
						data-testid={`payment-firstname-${id}`}
						onChange={handlePassengersNameFieldsChange}
					/>
				</div>
			</div>
		</div>
	);
};

PaymentIdentityFieldset.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	isRequired: PropTypes.bool,
};

export default memo(PaymentIdentityFieldset);
